import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import PromptLabLayout from "./PromptLabLayout.js";
import CanvasLayout from "./CanvasLayout.js";
import EditorLayout from "./EditorLayout.js";
import TemplateStudioLayout from "./TemplateStudioLayout.js";
import WebLabLayout from "./WebLabLayout.js";
import HotspotLayout from "./HotspotLayout.js";
import ChecklistLayout from "./ChecklistLayout.js";
import HudWalkthrough from "./HudWalkthrough.js";
import { usePageTitle } from "../A11y/pageTitle.js";
import { buildHudWalkthroughSteps } from "./hudWalkthroughSteps.js";
// The walkthrough replay pill is rendered here, not in HudWalkthrough, so this
// file needs the stylesheet in its own right.
import "./HudWalkthrough.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCircleQuestion,
    faComments,
    faEye,
    faLightbulb,
    faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
    requestInitClaudeCourseLesson,
    requestPromptLabClaude,
    requestJudgePromptLesson,
    requestBuildSpecContext,
} from "../../services/RobotAIServices.js";
import {
    requestUserLessonContent,
    setUserLessonContent,
    setLessonAsCompleted,
    requestAnonSetCourse,
    requestExecuteCode,
    requestMyProject,
    saveMyProject,
} from "../../services/userService.js";
import { buildAuthoredStarter, buildAuthoredExercise, buildAuthoredScreen } from "../../starterTemplate/authoredLessons.js";
import { buildStarterAppLabels } from "../../starterTemplate/starterAppHtml.js";
import {
    isBuildSpecLesson,
    buildSpecRubric,
    buildSpecExercise,
    buildSpecChatInstructions,
} from "./buildSpecLesson.js";
import { appIdeaRubric, appIdeaExercise } from "./appIdeaLesson.js";
import { isFinaleLesson, finaleRubric, finaleExercise } from "./finaleLesson.js";
import { buildSpecDownloadZip } from "./buildSpecDownload.js";
import { DEF_VERSION as CD01_DEF_VERSION } from "../../pages/claudeDevCourse/devLessonsRegistry.js";
import { buildAuthoredCanvas } from "../../pages/claudeDevCourse/authoredCanvases.js";
import { buildAuthoredChecklist } from "../../pages/claudeDevCourse/authoredChecklists.js";
import { useUserAuth } from "../../context/UserContext.js";
import { getFreshIdToken } from "../../utils/authToken.js";
import { isPrivilegedRole } from "../../utils/roles.js";
import { loadDraft, saveDraft, clearDraft } from "./chatDraft.js";
import { extractFirstCodeBlock } from "../../utils/extractCode.js";
import { prefersReducedMotion } from "./workspaceA11y.js";
import logoVideo from "../../assets/videos/loadingCircleBlackOnWhite1080.mp4";

/**
 * PromptLabHubComponent
 *
 * Orchestrates a single Claude Course lesson. On mount it loads (or generates +
 * caches) the lesson definition, then manages the chat with Claude, the code
 * editor state, and the submit/grade flow.
 *
 * Props:
 *   lessonMeta              — entry from CLAUDE_COURSE_LESSONS (the static registry)
 *   setallCheckboxesCompleted(boolean) — parent flag used to reveal Next button
 *   setCurrentCode          — App-level state exposing current code to the global AiAssistant
 *   setCurrentOuput         — App-level state exposing current console output
 *   aiOpenHelpRef           — ref to open the global AI help bubble (unused here)
 */
const MESSAGE_CAP_PER_SESSION = 30;

const normalizeText = (s) => (s == null ? "" : String(s).replace(/\r\n/g, "\n").trimEnd());

// Every criterion met, not merely enough of them. The judge passes a lesson at
// 80%, which is the right bar for moving on but the wrong one for the build-spec
// reward: an app whose spec still has a hole in it is what that lesson exists to
// prevent. Both the score and the per-criterion list are checked because either
// one alone has been seen to disagree with the other.
const isPerfect = (judgeResult) =>
    !!judgeResult &&
    judgeResult.score === 100 &&
    Array.isArray(judgeResult.perCriterion) &&
    judgeResult.perCriterion.length > 0 &&
    judgeResult.perCriterion.every((c) => c.passed);

// The student's saved app, used to brand the Module 4 scaffolds. Best-effort: a
// student who skipped CD107/V2101 simply gets the generic defaults, so a failure
// here must never stop a lesson from loading.
const loadProject = async (user) => {
    const token = await getFreshIdToken(user?.accessToken);
    if (!token) return null;
    try {
        return await requestMyProject(token);
    } catch (e) {
        console.warn("Could not load project for lesson starter:", e);
        return null;
    }
};

// Compact, plain-text brief of the lesson the student is currently on. Prepended to
// the lesson's own `systemPromptForClaude` at send time so the Prompt Lab chat knows
// which lesson/exercise this is and — on canvas lessons — what the student has done on
// the Concept Canvas. Without it Claude answers blind and asks them to paste the
// instructions. Every field is guarded, so lessons missing a field (or the whole
// canvas) simply drop that line. The embedded copy is already localized; the trailing
// `systemPromptForClaude` still fixes the reply language, so this stays neutral.
const buildLessonContextBlock = (definition, canvasState) => {
    if (!definition) return "";

    const lines = ["THE LESSON THE STUDENT IS CURRENTLY ON:"];

    const title = (definition.title || "").trim();
    const m = /M(\d+).*?L(\d+)/i.exec(definition.lessonID || "");
    const where = m ? ` (Module ${m[1]} · Lesson ${m[2]})` : "";
    if (title) lines.push(`- Title: ${title}${where}`);

    const markdown = (definition.lessonMarkdown || "").trim();
    if (markdown) lines.push(`- Lesson content:\n${markdown}`);

    const exercise = (definition.exercisePrompt || "").trim();
    if (exercise) lines.push(`- The student's exercise: ${exercise}`);

    const hint = (definition.hint || "").trim();
    if (hint) lines.push(`- Hint they can reveal: ${hint}`);

    // The on-screen Concept Canvas (canvas lessons only).
    const canvas = definition.canvas;
    const nodes = Array.isArray(canvas?.nodes) ? canvas.nodes : [];
    if (nodes.length > 0) {
        const kind =
            canvas.mode === "order"
                ? "an ordering exercise — the parts below are listed in the CORRECT order (the UI shuffles them for the student to drag back)"
                : "an explore exercise — the student taps each part to reveal it";
        lines.push(`- On-screen Concept Canvas (${kind}):`);
        nodes.forEach((n, i) => {
            const label = (n.label || "").trim();
            const desc = (n.description || "").trim();
            lines.push(`   ${i + 1}. ${label}${desc ? ` — ${desc}` : ""}`);
        });
    }

    // The student's live progress on that canvas, if the layout reported any.
    if (canvasState?.mode === "explore") {
        lines.push(
            canvasState.allExplored
                ? `- Canvas progress: the student has explored all ${canvasState.total} parts.`
                : `- Canvas progress: explored ${canvasState.exploredLabels?.length || 0}/${canvasState.total}` +
                  (canvasState.remainingLabels?.length
                      ? ` — still to explore: ${canvasState.remainingLabels.join(", ")}.`
                      : ".")
        );
    } else if (canvasState?.mode === "order") {
        lines.push(
            `- Canvas progress: their current order is ${(canvasState.currentOrderLabels || []).join(" → ")}` +
            (canvasState.solved ? " — and it's correct/solved." : " — not yet in the correct order.")
        );
    }

    lines.push(
        "",
        "Use this to help the student with THEIR current lesson and exercise: refer to the specific parts and their progress above, and guide them rather than just handing over the final answer."
    );

    return lines.join("\n") + "\n\n";
};

const PromptLabHubComponent = ({
    lessonMeta,
    setallCheckboxesCompleted,
    setCurrentCode,
    setCurrentOuput,
}) => {
    const { user, signInAnonUser } = useUserAuth();
    const { t, i18n } = useTranslation();

    // Language the lesson content should be generated / displayed in. Kept as the
    // bare locale ("en", "es") so it matches the i18next `languageOnly` load.
    const locale = (i18n.language || "en").split("-")[0];

    // Schema version to validate a cached definition against. This hub serves both
    // CD01 and CD02, which version independently, so it must come from the lesson's
    // own registry entry — hard-coding either course's constant would make the
    // other course's cache look permanently stale and regenerate on every visit.
    // The CD01 fallback covers the legacy CC course, whose registry has no version.
    const defVersion = lessonMeta?.defVersion ?? CD01_DEF_VERSION;

    // Walkthrough copy is localized; rebuild only when the language changes so the
    // steps array keeps a stable identity between renders.
    const hudSteps = useMemo(() => buildHudWalkthroughSteps(t), [i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

    // Lesson definition (AI-generated, cached in Firestore)
    const [definition, setDefinition] = useState(null);
    const [isLoadingLesson, setIsLoadingLesson] = useState(true);
    const [loadError, setLoadError] = useState(null);

    // Chat with Claude. Seeded from the localStorage draft on the very first render
    // rather than in an effect, so a reload never shows an empty transcript that
    // then pops back — and so the autosave effect below can't run against []
    // before the restore lands and wipe the draft it was about to restore.
    const draftKeyUid = user?.uid || null;
    const draftLessonID = lessonMeta?.lessonID || null;
    const [messages, setMessages] = useState(() => loadDraft(draftKeyUid, draftLessonID));
    const [input, setInput] = useState("");
    const [isThinking, setIsThinking] = useState(false);

    // How many messages were already on screen when this mount began. Restored
    // ones must not spend the per-session cap: before drafts existed a reload
    // reset that budget, and the cap guards cost per sitting, not the lifetime of
    // a lesson. Without this baseline a student who reloaded a long build-spec
    // transcript would come back to an input that silently does nothing.
    const sessionBaselineRef = useRef(null);
    if (sessionBaselineRef.current === null) sessionBaselineRef.current = messages.length;

    // Code lessons
    const [editorCode, setEditorCode] = useState("");
    const [consoleOutput, setConsoleOutput] = useState("");
    const [testResults, setTestResults] = useState(null);
    // True while Run executes; the layouts turn it into a spoken summary.
    const [isRunning, setIsRunning] = useState(false);

    // Concept lessons
    const [judgeResult, setJudgeResult] = useState(null);
    const [isJudging, setIsJudging] = useState(false);

    // The build-spec lessons (V2401 / V3401) only. What Module 1 already settled
    // about the student's app: `{ spec, covered, rubric }`, where `rubric` carries
    // the per-slot metadata the rubric panel needs to tag pre-credited lines.
    // `definition.rubric` stays a plain string[] — that is the judge's contract.
    const [specContext, setSpecContext] = useState(null);

    // Their saved project, kept for the build-spec download (it brands the app).
    const [project, setProject] = useState(null);
    const [isPreparingDownload, setIsPreparingDownload] = useState(false);

    // Module 6 hotspot lessons — the finished replica the student clicks. Kept apart
    // from `editorCode` because it is not code and is never edited; it lands here
    // rather than in the layout because it carries the student's app name, and the
    // `users_projects` record that holds it is loaded in the init effect below.
    const [authoredScreen, setAuthoredScreen] = useState(null);

    // Completion
    const [allPassed, setAllPassed] = useState(false);

    // First-time HUD walkthrough — bump to replay on demand.
    const [replaySignal, setReplaySignal] = useState(0);

    // The lesson hint. It used to sit inline at the bottom of the lesson card in
    // every layout; it now rides in the navbar dock next to the Walkthrough pill,
    // so a stuck student doesn't have to scroll the lesson pane to find it. It is
    // rendered here for the same reason the walkthrough pill is: the layouts are
    // not siblings of the navbar and cannot portal into it themselves.
    const [hintOpen, setHintOpen] = useState(false);
    // The hint text stays behind a click: the popover leads with the ways out (ask
    // Claude, have it done for you), so a student who wanted a nudge doesn't get
    // the answer spoiled by opening the panel.
    const [hintRevealed, setHintRevealed] = useState(false);
    const hintRef = useRef(null);
    const hintToggleRef = useRef(null);
    const hintPopRef = useRef(null);
    const hintAnswerRef = useRef(null);
    // Bumped to tell a layout that owns its own Claude panel / work area to ask for
    // help, or to fill the exercise in. Same one-way signal the walkthrough replay
    // uses: the hub can't reach into the layout's state, and shouldn't.
    const [askHelpSignal, setAskHelpSignal] = useState(0);
    const [solveSignal, setSolveSignal] = useState(0);
    const [solving, setSolving] = useState(false);

    // The admin/tester "Mark lesson complete" shortcut is rendered into the
    // lesson footer (between Back and Next) via a portal. This holds that DOM
    // target, looked up after mount from the sibling LessonFooterNav.
    const [testerSlot, setTesterSlot] = useState(null);

    // Same idea for the walkthrough replay pill, which is portaled into the
    // sibling LessonNavBar so it stops floating over the panel headers.
    const [walkthroughSlot, setWalkthroughSlot] = useState(null);

    const didInitRef = useRef(false);

    // Latest Concept Canvas progress snapshot (canvas lessons only). Held in a ref
    // — not state — because it feeds the next chat request, not the render, so
    // updating it must not re-render the hub. The callback is stable so the child
    // effect that reports progress isn't re-subscribed on every render.
    const canvasStateRef = useRef(null);
    const handleCanvasState = useCallback((snapshot) => {
        canvasStateRef.current = snapshot;
    }, []);

    // Persist the transcript on every turn, so a reload picks up where the student
    // left off instead of throwing the conversation away. See chatDraft.js for why
    // this is localStorage and not a write per message. Cleared on a pass, below.
    useEffect(() => {
        saveDraft(draftKeyUid, draftLessonID, messages);
    }, [draftKeyUid, draftLessonID, messages]);

    // ----------------------------------------------------------------------
    // Load (or generate) the lesson definition once per lesson per user
    // ----------------------------------------------------------------------
    useEffect(() => {
        if (!lessonMeta) return;
        if (didInitRef.current) return;
        didInitRef.current = true;

        const init = async () => {
            try {
                let currentUser = user;
                if (!currentUser) {
                    currentUser = await signInAnonUser();
                    try {
                        await requestAnonSetCourse("ClaudeCourse", currentUser.uid, currentUser.accessToken);
                    } catch (e) {
                        // best-effort; not fatal for loading the lesson
                        console.warn("Anon course registration failed:", e);
                    }
                }

                // Try Firestore cache first
                let cached = null;
                try {
                    cached = await requestUserLessonContent(currentUser, lessonMeta.lessonID, locale);
                } catch (e) {
                    console.warn("Cache read failed:", e);
                }

                let def = cached?.content || null;

                // Invalidate stale cached definitions for lessons that declare a
                // grading model (the CD course). Older caches predate `grading` /
                // `defVersion`, so bumping DEF_VERSION forces a one-time regen. We
                // also regenerate when the cached content is in a different language
                // than the current UI (legacy/seeded content has no `locale`, so it
                // is treated as English).
                if (
                    def &&
                    lessonMeta?.grading &&
                    (def.defVersion !== defVersion || (def.locale || "en") !== locale)
                ) {
                    def = null;
                }

                if (!def) {
                    // Generate fresh via Claude
                    def = await requestInitClaudeCourseLesson(
                        lessonMeta.lessonID,
                        lessonMeta.title,
                        lessonMeta.type,
                        lessonMeta.language || "",
                        lessonMeta.layout || "",
                        lessonMeta.grading || "",
                        locale
                    );

                    if (!def || typeof def !== "object") {
                        throw new Error("Lesson generator returned no definition");
                    }

                    // Stamp meta into the definition so it travels with the cache
                    def.lessonID = def.lessonID || lessonMeta.lessonID;
                    def.title = def.title || lessonMeta.title;
                    def.type = def.type || lessonMeta.type;
                    def.language = def.language || lessonMeta.language || "";
                    def.grading = def.grading || lessonMeta.grading || "";
                    def.defVersion = defVersion;
                    def.locale = locale;

                    try {
                        await setUserLessonContent(currentUser, lessonMeta.lessonID, def, null);
                    } catch (e) {
                        console.warn("Cache write failed:", e);
                    }
                }

                // Module 1 canvas lessons grade by exact node order, so the "correct"
                // order must be AUTHORED, not left to per-user AI generation — a
                // generated order that doesn't match the card descriptions is
                // impossible to solve. Pin it here; overriding on read wins over
                // whatever the cache/generator produced (no version bump needed).
                if (lessonMeta?.layout === "canvas") {
                    const authoredCanvas = buildAuthoredCanvas(lessonMeta.lessonID, locale);
                    if (authoredCanvas) def.canvas = authoredCanvas;
                }

                // Web Lab lessons with an authored scaffold need their INSTRUCTIONS
                // authored too. The generator writes starterCode, domTests and the
                // exercise as one matched set, but the two below are replaced with
                // the authored pair — so generated prose describes a page the
                // student never sees (V3303 asked for `.card` and a button on a page
                // that has neither). Authored exercise wins, same as the canvas.
                // `lessonMarkdown` is left alone: it teaches the concept rather than
                // the page, and its pregenerated narration audio is keyed to it.
                const authoredExercise = buildAuthoredExercise(lessonMeta.lessonID, locale);
                if (authoredExercise) {
                    def.exercisePrompt = authoredExercise.exercisePrompt;
                    def.hint = authoredExercise.hint;
                }

                // Same rule for the install checklist (V2602): the generator still
                // writes an exercise for a canvas it no longer gets, so the authored
                // pair that ships WITH the checklist wins. Kept out of
                // AUTHORED_EXERCISES because the checklist is not a Web Lab lesson and
                // has no COPY entry to derive from.
                if (lessonMeta?.layout === "checklist") {
                    const authoredChecklist = buildAuthoredChecklist(lessonMeta.lessonID, locale);
                    if (authoredChecklist) {
                        def.exercisePrompt = authoredChecklist.exercise;
                        def.hint = authoredChecklist.hint;
                    }
                }

                // The app-idea lesson is graded on the "My app idea" card, not on
                // the chat, so its rubric has to be authored: a generated one is
                // written for a transcript and fails a student who filled the card
                // in and never opened the chat. Pinned on read like the canvas and
                // the build-spec rubric — no cache purge, no DEF_VERSION bump. See
                // appIdeaLesson.js for why it is deliberately lenient.
                if (lessonMeta?.captureProject) {
                    const exercise = appIdeaExercise(locale);
                    def.rubric = appIdeaRubric(locale);
                    def.exercisePrompt = exercise.exercisePrompt;
                    def.hint = exercise.hint;
                }

                // The build-spec lesson is the second half of a pair: Module 1 had
                // the student choose an app, this turns it into a spec. Ask the
                // backend which parts of a spec they already settled there, then
                // pin a rubric and an exercise built around what's LEFT — a
                // generated rubric re-asked for the app idea they chose weeks ago.
                // Coverage is empty for anyone who skipped Module 1, which restores
                // this lesson's original behavior exactly.
                if (isBuildSpecLesson(lessonMeta.lessonID)) {
                    const { spec, covered } = await requestBuildSpecContext(
                        await getFreshIdToken(currentUser?.accessToken)
                    );
                    const rubric = buildSpecRubric(locale, covered);
                    const exercise = buildSpecExercise(locale, covered);

                    def.rubric = rubric.map((r) => r.criterion);
                    def.exercisePrompt = exercise.exercisePrompt;
                    def.hint = exercise.hint;

                    setSpecContext({ spec, covered: covered || {}, rubric });
                }

                // The closing lesson is graded on one line — did they say what they
                // want to pursue next — instead of the five the generator writes for
                // it. Pinned on read like the two above; see finaleLesson.js for why
                // the last page of the course grades that lightly.
                if (isFinaleLesson(lessonMeta.lessonID)) {
                    const exercise = finaleExercise(locale);
                    def.rubric = finaleRubric(locale);
                    def.exercisePrompt = exercise.exercisePrompt;
                    def.hint = exercise.hint;
                }

                setDefinition(def);

                // Module 4 lessons must start from the PREVIOUS lesson's app, so the
                // student grows one product instead of doing unrelated exercises. That
                // chain is authored in authoredLessons.js rather than generated — a
                // regenerated lesson would otherwise invent a different app each time
                // and break the thread back to the day-one branding. The authored
                // scaffold therefore wins over whatever the generator produced.
                // One load, three consumers: the Web Lab scaffolds and the Module 6
                // replicas both stamp the student's own app name into the document,
                // and the build-spec download brands the app it hands them.
                const loadedProject = await loadProject(currentUser);
                setProject(loadedProject);

                const authored = buildAuthoredStarter(lessonMeta.lessonID, loadedProject, locale);
                if (authored) setEditorCode(authored);
                else if (def?.starterCode) setEditorCode(def.starterCode);

                // Module 6's click-to-identify screens. Authored only — there is no
                // generated fallback, and a lesson without one renders an empty state
                // rather than an unanswerable exercise.
                setAuthoredScreen(buildAuthoredScreen(lessonMeta.lessonID, loadedProject, locale));

                // Hydrate completion if already finished
                if (cached?.lessonCompleted) {
                    setAllPassed(true);
                    setallCheckboxesCompleted?.(true);
                }
            } catch (e) {
                console.error("Failed to initialize Claude Course lesson:", e);
                setLoadError(e?.message || String(e));
            } finally {
                setIsLoadingLesson(false);
            }
        };

        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lessonMeta?.lessonID]);

    // ----------------------------------------------------------------------
    // Keep parent App state in sync (so the global AiAssistant can see things)
    // ----------------------------------------------------------------------
    useEffect(() => {
        setCurrentCode?.(editorCode);
    }, [editorCode, setCurrentCode]);

    useEffect(() => {
        setCurrentOuput?.(consoleOutput);
    }, [consoleOutput, setCurrentOuput]);

    // Privileged roles (admin/tester) can move on freely: once the lesson has
    // loaded, reveal the parent "Next" button without persisting completion and
    // without flipping `allPassed` (so the grade/run buttons stay usable for QA).
    useEffect(() => {
        if (definition && isPrivilegedRole(user?.role)) {
            setallCheckboxesCompleted?.(true);
        }
    }, [definition, user, setallCheckboxesCompleted]);

    // Locate the portal targets once mounted (the sibling LessonFooterNav and
    // LessonNavBar are committed in the same pass, so their DOM nodes exist by
    // the time effects run): the tester shortcut goes between Back and Next, and
    // the walkthrough pill goes into the navbar beside the profile avatar.
    useEffect(() => {
        setTesterSlot(document.getElementById("lesson-footer-tester-slot"));
        setWalkthroughSlot(document.getElementById("lesson-walkthrough-dock"));
    }, []);

    // Closing the hint popover from inside it (Escape, one of its actions) hands
    // focus back to its pill. Not when focus has already gone somewhere else.
    const closeHint = useCallback(() => {
        const active = document.activeElement;
        const inside = !active || active === document.body || hintRef.current?.contains(active);
        setHintOpen(false);
        if (inside) hintToggleRef.current?.focus();
    }, []);

    // Hint popover: dismiss on an outside click or Escape (it overlays the lesson,
    // so leaving it pinned open would sit on top of the work), and on a lesson
    // change, so a new lesson never opens with the previous hint showing.
    useEffect(() => {
        if (!hintOpen) return undefined;
        const onPointerDown = (e) => {
            if (hintRef.current && !hintRef.current.contains(e.target)) setHintOpen(false);
        };
        const onKeyDown = (e) => {
            if (e.key === "Escape") closeHint();
        };
        document.addEventListener("pointerdown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("pointerdown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [hintOpen, closeHint]);

    // Opening the popover moves focus to its first action.
    useEffect(() => {
        if (hintOpen) hintPopRef.current?.querySelector("button:not(:disabled)")?.focus();
    }, [hintOpen]);

    // "Show me the hint" removes its own button, so focus goes to the hint.
    useEffect(() => {
        if (hintRevealed) hintAnswerRef.current?.focus();
    }, [hintRevealed]);

    useEffect(() => {
        setHintOpen(false);
        setHintRevealed(false);
    }, [definition?.hint]);

    // ----------------------------------------------------------------------
    // Send a message to Claude
    // ----------------------------------------------------------------------
    // `overrideText` lets a caller send a message the student didn't type — the
    // "Stuck?" popover's ask-for-help button uses it. Layouts wire onSend straight
    // to a click handler, so anything that isn't a string (a click event) is
    // ignored and the composer's own text is sent instead.
    const handleSend = async (overrideText) => {
        const outgoing = (typeof overrideText === "string" ? overrideText : input).trim();
        if (!outgoing || !definition || isThinking) return;
        if (messages.length - sessionBaselineRef.current >= MESSAGE_CAP_PER_SESSION) {
            console.warn("Per-session message cap reached.");
            return;
        }

        const userMessage = { role: "user", content: outgoing };
        const nextMessages = [...messages, userMessage];
        setMessages(nextMessages);
        // Only the composer's own text is cleared — an override must not throw away
        // a half-written message the student still means to send.
        if (typeof overrideText !== "string") setInput("");
        setIsThinking(true);

        try {
            // Ground Claude in the current lesson + live canvas progress. The
            // lesson's own behavior prompt stays LAST so its "reply in {language}"
            // instruction remains the final, authoritative word.
            //
            // On the build-spec lesson the slot instructions come after it: they say
            // which parts of the spec are already decided and which this
            // conversation is for, and they must outrank the generated prompt's
            // habit of opening with "so, what do you want to build?". The student's
            // actual Module 1 decisions are prepended server-side.
            const system =
                buildLessonContextBlock(definition, canvasStateRef.current) +
                (definition.systemPromptForClaude || "") +
                (specContext ? buildSpecChatInstructions(locale, specContext.covered) : "");

            const result = await requestPromptLabClaude(
                system,
                nextMessages,
                definition.lessonID,
                // Fresh, not the sign-in snapshot: an expired token here doesn't
                // fail the call, it makes the backend drop the student's project
                // from the brief, and Claude quietly forgets their app mid-lesson.
                await getFreshIdToken(user?.accessToken)
            );

            const assistantText = result?.text || t("promptLab.noResponse");
            const assistantMsg = { role: "assistant", content: assistantText };
            setMessages((prev) => [...prev, assistantMsg]);

            // For code lessons, pull the first matching code block into the editor
            if (definition.type === "code") {
                const block = extractFirstCodeBlock(assistantText, definition.language);
                if (block?.code) {
                    setEditorCode(block.code);
                    setConsoleOutput("");
                    setTestResults(null);
                }
            }
        } catch (e) {
            console.error("Claude call failed:", e);
            setMessages((prev) => [
                ...prev,
                { role: "assistant", content: t("promptLab.errorReachingClaude") },
            ]);
        } finally {
            setIsThinking(false);
        }
    };

    // ----------------------------------------------------------------------
    // Run the code in the editor (code lessons). For test-graded lessons Run is
    // also the grader: it executes every test case, records pass/fail for the
    // exercise badge, and surfaces one real program run in the console (the first
    // failing case, else the first case). The console stays program-output only —
    // per-test detail is intentionally not shown (students can "Ask Claude").
    // ----------------------------------------------------------------------
    const handleRun = async () => {
        setIsRunning(true);
        try {
            await runCode();
        } finally {
            setIsRunning(false);
        }
    };

    const runCode = async () => {
        if (!definition || definition.type !== "code") return;
        if (!editorCode?.trim()) {
            setConsoleOutput(t("promptLab.editorEmpty"));
            setTestResults(null);
            return;
        }

        const lang = definition.language || "python";
        const cases = Array.isArray(definition.testCases) ? definition.testCases : [];

        // No test cases to grade against → plain run with no stdin.
        if (cases.length === 0) {
            setConsoleOutput(t("promptLab.running"));
            try {
                const result = await requestExecuteCode(lang, editorCode, "");
                const out = (result?.stdout || "") + (result?.stderr ? `\n[stderr]\n${result.stderr}` : "");
                setConsoleOutput(out || t("promptLab.noOutput"));
            } catch (e) {
                setConsoleOutput(t("promptLab.executionFailed", { msg: e?.message || e }));
            }
            return;
        }

        setConsoleOutput(t("promptLab.running"));
        setTestResults(null);

        const results = [];
        const consolePerCase = [];
        for (const tc of cases) {
            try {
                const res = await requestExecuteCode(lang, editorCode, tc.stdin || "");
                const actual = normalizeText(res?.stdout || "");
                const expected = normalizeText(tc.expectedStdout || "");
                results.push({ passed: actual === expected });
                consolePerCase.push((res?.stdout || "") + (res?.stderr ? `\n[stderr]\n${res.stderr}` : ""));
            } catch (e) {
                results.push({ passed: false });
                consolePerCase.push(t("promptLab.executionFailed", { msg: e?.message || e }));
            }
        }

        // Console shows the first failing run (most useful), else the first run.
        const firstFail = results.findIndex((r) => !r.passed);
        const shown = firstFail === -1 ? 0 : firstFail;
        setConsoleOutput(consolePerCase[shown] || t("promptLab.noOutput"));
        setTestResults(results);

        if (results.every((r) => r.passed)) await markComplete();
    };

    // ----------------------------------------------------------------------
    // Submit for grading — rubric (conversation) lessons only. Test-graded code
    // lessons are graded inline by Run (see handleRun) and no longer expose a
    // separate submit button; route here defensively for any legacy caller.
    // ----------------------------------------------------------------------
    const handleSubmit = async () => {
        if (!definition) return;

        // Grading is authoritative; fall back to the legacy type-based branch for
        // older lessons (the CC course) that don't declare a grading model.
        const mode = definition.grading || (definition.type === "code" ? "tests" : "rubric");
        if (mode === "tests") {
            await handleRun();
        } else {
            await runJudge();
        }
    };

    const runJudge = async () => {
        // The app-idea lesson is graded on the saved "My app idea" card instead of
        // the transcript (appIdeaLesson.js). The card is loaded server-side from the
        // caller's token — never posted from here — so a student can't submit
        // someone else's idea, or an idea they never saved.
        const gradeProject = !!lessonMeta?.captureProject;

        if (!Array.isArray(definition.rubric) || definition.rubric.length === 0) {
            setJudgeResult({
                passed: false,
                score: 0,
                feedback: t("promptLab.missingRubric"),
                perCriterion: [],
            });
            return;
        }
        // Only lessons graded on the conversation need one. Requiring a chat on the
        // app-idea lesson would grade the thing the rubric no longer reads.
        if (!gradeProject && messages.length === 0) {
            setJudgeResult({
                passed: false,
                score: 0,
                feedback: t("promptLab.haveConversationFirst"),
                perCriterion: [],
            });
            return;
        }

        setIsJudging(true);
        try {
            // Nothing saved yet — the judge would be handed an empty card and fail
            // every line for no reason the student can act on. Say "save it first"
            // instead, and don't spend a Claude call. Best-effort read: on a fetch
            // failure loadProject returns null and we nudge, which at worst asks
            // them to press Save again.
            if (gradeProject) {
                const saved = await loadProject(user);
                if (!saved?.appName?.trim()) {
                    setJudgeResult({
                        passed: false,
                        feedback: t("promptLab.saveIdeaFirst"),
                        perCriterion: [],
                    });
                    return;
                }
            }

            // One refresh for the whole submit flow — the judge call and the two
            // saves below. Stale tokens made grading fail outright here, and made
            // it fail in the least helpful way: "spec-aware grading" lost the prior
            // spec, so Module 1's settled slots were marked unmet.
            const token = await getFreshIdToken(user?.accessToken);

            const result = await requestJudgePromptLesson(
                definition.lessonID,
                definition.rubric,
                messages,
                locale,
                // The build-spec lesson is graded on Module 1 AND this conversation,
                // so a slot the student already settled passes without being redone.
                // The app-idea lesson is graded on the card the student saved. Both
                // are loaded server-side from the token, never sent from here.
                { token, usePriorSpec: !!specContext, gradeProject }
            );
            const safe = result || {
                passed: false,
                score: 0,
                feedback: t("promptLab.judgeNoResult"),
                perCriterion: [],
            };
            // Only a result the AI actually produced gets the "Graded by AI"
            // note; the hub's own messages (no conversation yet, judge
            // unreachable, ...) are shown in the same box but are not grades.
            setJudgeResult(result ? { ...safe, aiGraded: true } : safe);

            // Keep the conversation too, not just the four card fields. Module 4's
            // build-spec lesson reads it back to see which parts of a spec the student
            // already settled here, so it can grade what's still missing instead of
            // asking for the app idea a second time. Saved only on a pass — and only
            // when there IS one, since the chat is optional on this lesson and writing
            // an empty array would wipe a transcript from an earlier visit. Best-effort:
            // losing it costs personalization in one later lesson, never this one's
            // completion.
            if (lessonMeta?.captureProject && safe.passed && messages.length > 0 && token) {
                try {
                    await saveMyProject({ ideaConversation: messages }, token);
                } catch (e) {
                    console.warn("Could not save the idea conversation:", e);
                }
            }

            // Remember a perfect build spec. Completing the lesson locks the chat, so
            // without this a student who closed the tab between passing and clicking
            // Download could never reach their app again — they cannot re-submit to
            // earn the button back.
            if (lessonMeta?.downloadOnPerfect && isPerfect(safe) && token) {
                try {
                    await saveMyProject({ specComplete: true }, token);
                    setProject((prev) => ({ ...(prev || {}), specComplete: true }));
                } catch (e) {
                    console.warn("Could not record the finished build spec:", e);
                }
            }

            if (safe.passed) {
                // Drop the draft only when passing actually ends the chat — the same
                // condition PromptLabLayout uses to lock it. On the build-spec lesson
                // (`downloadOnPerfect`) and the app-idea lesson (`captureProject`) the
                // chat deliberately stays open after a pass, because the download is
                // earned above the pass mark; clearing there would cost a student who
                // passed at 83% the very conversation they still need.
                const chatEnds = !lessonMeta?.downloadOnPerfect && !lessonMeta?.captureProject;
                if (chatEnds) clearDraft(draftKeyUid, draftLessonID);
                await markComplete();
            }
        } catch (e) {
            console.error("Judge call failed:", e);
            setJudgeResult({
                passed: false,
                score: 0,
                feedback: t("promptLab.judgeUnreachable"),
                perCriterion: [],
            });
        } finally {
            setIsJudging(false);
        }
    };

    // The build-spec lesson's reward, and the only place a promptLab lesson offers
    // a download: the student's branded Starter App Template as a project folder,
    // with the spec they just finished written into its CLAUDE.md. See
    // buildSpecDownload.js for why it is a folder rather than the single file it was.
    const handleDownloadApp = async () => {
        if (isPreparingDownload) return;
        setIsPreparingDownload(true);
        try {
            // Re-extract with THIS lesson's transcript folded in, so the spec in the
            // project is the finished one rather than Module 1's half. On any failure
            // fall back to the spec already loaded — a slightly thinner CLAUDE.md is
            // much better than a download that doesn't happen.
            let spec = specContext?.spec || null;
            try {
                const fresh = await requestBuildSpecContext(
                    await getFreshIdToken(user?.accessToken),
                    messages
                );
                if (fresh?.spec) spec = fresh.spec;
            } catch (e) {
                console.warn("Could not refresh the build spec for download:", e);
            }

            const { bytes, filename } = buildSpecDownloadZip({
                project,
                spec,
                locale,
                labels: buildStarterAppLabels(t),
                specLabels: {
                    title: t("buildSpec.file.title"),
                    appName: t("buildSpec.file.appName"),
                    whatItDoes: t("buildSpec.file.whatItDoes"),
                    whoItsFor: t("buildSpec.file.whoItsFor"),
                    features: t("buildSpec.file.features"),
                    data: t("buildSpec.file.data"),
                    screens: t("buildSpec.file.screens"),
                    actions: t("buildSpec.file.actions"),
                    notInV1: t("buildSpec.file.notInV1"),
                    nextStep: t("buildSpec.file.nextStep"),
                },
            });

            // `bytes` is a view on a larger ArrayBuffer only if it were subarray'd —
            // it isn't, but slicing keeps the Blob honest if that ever changes.
            const blob = new Blob([bytes.slice()], { type: "application/zip" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } finally {
            setIsPreparingDownload(false);
        }
    };

    const markComplete = async () => {
        setAllPassed(true);
        setallCheckboxesCompleted?.(true);
        try {
            if (user?.uid) await setLessonAsCompleted(definition.lessonID, user.uid);
        } catch (e) {
            console.warn("Failed to persist completion:", e);
        }
    };

    // Localized lesson title (registry lessonID → cdTitles.*), falling back to the
    // AI/registry English title if a translation is missing. It also names the
    // browser tab and the route announcement, from before the lesson loads.
    const lessonTitle = t(`cdTitles.${lessonMeta?.lessonID}`, {
        defaultValue: definition?.title || lessonMeta?.title,
    });
    usePageTitle(lessonTitle);

    // ----------------------------------------------------------------------
    // Render
    // ----------------------------------------------------------------------
    if (loadError) {
        return (
            <div style={{ padding: "2rem", color: "#ff7a7a" }}>
                <h4>{t("promptLab.couldNotLoad")}</h4>
                <p>{loadError}</p>
            </div>
        );
    }

    if (isLoadingLesson || !definition) {
        return (
            <div className="courses-loading-overlay" role="alert" aria-busy="true" aria-live="polite">
                <div className="courses-loading-card">
                    {/* Decorative. It stays still for people who ask for less motion. */}
                    <video
                        src={logoVideo}
                        autoPlay={!prefersReducedMotion()}
                        loop
                        muted
                        playsInline
                        draggable={false}
                        aria-hidden="true"
                        style={{ width: 72, height: 72, display: "block" }}
                    />
                    <div className="courses-loading-text">{t("promptLab.generatingLesson")}</div>
                </div>
            </div>
        );
    }

    // Pick the layout. `layout` comes from the lesson registry; the original
    // "Use Claude Like a Developer" course has none, so it falls through to the
    // default Prompt Lab layout (which handles both code and concept lessons).
    const layout = lessonMeta?.layout;
    // The app-idea lesson rearranges the promptLab layout — the card takes the
    // middle column and the chat sits under the rubric — so it gets its own tour.
    // The shared one would point at the form and call it the chat, on the very
    // first screen of the course.
    const hud = lessonMeta?.captureProject ? "ideaCard" : layout || "promptLab";

    // The two lessons that cannot be answered for the student: the app idea
    // (captureProject) and the build spec (downloadOnPerfect). Their answer IS the
    // student's own idea — it is what the downloadable app is generated from — so
    // "do it for me" would hand back somebody else's project. Keyed on the registry
    // flags, not lesson ids, so a future personalized lesson is covered by adding
    // the flag it already needs.
    const isPersonalLesson = !!(lessonMeta?.captureProject || lessonMeta?.downloadOnPerfect);

    let layoutEl;
    if (layout === "canvas") {
        layoutEl = (
            <CanvasLayout
                definition={definition}
                lessonTitle={lessonTitle}
                messages={messages}
                input={input}
                setInput={setInput}
                onSend={handleSend}
                isThinking={isThinking}
                onComplete={markComplete}
                allPassed={allPassed}
                onCanvasStateChange={handleCanvasState}
                solveSignal={solveSignal}
            />
        );
    } else if (layout === "webLab") {
        // Web lessons run and grade entirely in the browser (see WebLabLayout /
        // webLabHarness), so the layout owns the run loop and only reports back
        // when every DOM check passes. handleRun/OneCompiler is not involved.
        layoutEl = (
            <WebLabLayout
                definition={definition}
                lessonTitle={lessonTitle}
                editorCode={editorCode}
                setEditorCode={setEditorCode}
                onTestsPassed={markComplete}
                allPassed={allPassed}
                downloadable={lessonMeta?.downloadable}
                multiFile={lessonMeta?.multiFile}
                askHelpSignal={askHelpSignal}
                solveSignal={solveSignal}
            />
        );
    } else if (layout === "checklist") {
        // CD02 V2602: the install lesson. The work happens outside the app, so the
        // checklist tracks it rather than grading it — self-attested, and it says so.
        // The Prompt Lab stays below because an install is where a student is most
        // likely to meet an error message. See ChecklistLayout / authoredChecklists.
        //
        // Resolved inline rather than memoized because this sits after the loading and
        // error early-returns, where a hook would break hook order.
        // `buildAuthoredChecklist` hands back a module-level object, so its identity is
        // stable across renders — InstallChecklist keys an effect on it, and building a
        // fresh object here would reset the student's ticks on every render. Keep that
        // accessor returning a reference, not a copy.
        //
        // `definition.locale`, not the live i18n read, for the same reason WebLabLayout
        // and HotspotLayout use it: the init effect only re-runs on route change, so a
        // live read could flip the checklist copy out from under the lesson prose.
        layoutEl = (
            <ChecklistLayout
                definition={definition}
                lessonTitle={lessonTitle}
                checklist={buildAuthoredChecklist(definition.lessonID, definition.locale || locale)}
                messages={messages}
                input={input}
                setInput={setInput}
                onSend={handleSend}
                isThinking={isThinking}
                onComplete={markComplete}
                allPassed={allPassed}
                solveSignal={solveSignal}
            />
        );
    } else if (layout === "hotspot") {
        // CD02 Module 6: the student identifies parts of a finished replica of the
        // Claude Code desktop app by clicking them. Like webLab it grades entirely
        // in the browser and owns its own loop, so it only reports back once every
        // region has been found. See HotspotLayout / hotspotHarness.
        layoutEl = (
            <HotspotLayout
                definition={definition}
                lessonTitle={lessonTitle}
                screen={authoredScreen}
                onComplete={markComplete}
                allPassed={allPassed}
                askHelpSignal={askHelpSignal}
                solveSignal={solveSignal}
            />
        );
    } else if (layout === "template") {
        layoutEl = (
            <TemplateStudioLayout
                definition={definition}
                lessonTitle={lessonTitle}
                onComplete={markComplete}
                allPassed={allPassed}
            />
        );
    } else if (layout === "editor") {
        layoutEl = (
            <EditorLayout
                definition={definition}
                lessonTitle={lessonTitle}
                editorCode={editorCode}
                setEditorCode={setEditorCode}
                language={definition.language}
                consoleOutput={consoleOutput}
                onRun={handleRun}
                isRunning={isRunning}
                testResults={testResults}
                allPassed={allPassed}
                askHelpSignal={askHelpSignal}
                solveSignal={solveSignal}
            />
        );
    } else {
        // The build-spec lesson's payoff, gated on a perfect score (see isPerfect).
        // `specComplete` is the same thing earned on an earlier visit: completing the
        // lesson locks the chat, so a student who left before downloading has no way
        // to re-submit and would otherwise never get their app.
        const downloadReady =
            !!lessonMeta?.downloadOnPerfect && (isPerfect(judgeResult) || !!project?.specComplete);

        // Every other lesson locks its chat the moment it is passed, because passing
        // IS the end of it. A lesson with a reward above the pass mark is different:
        // the judge passes at 80%, so locking there strands the student a criterion
        // short of their app, reading feedback that tells them to keep talking to
        // Claude in a box they can no longer type in.
        //
        // Deliberately NOT "open until the reward is earned". Tying it to the reward
        // put the lock back the moment the student earned it — and on the next visit,
        // where the lesson loads already complete, they could neither refine the spec
        // nor re-download. This lesson's chat simply never locks.
        const chatStaysOpen = !!lessonMeta?.downloadOnPerfect;

        layoutEl = (
            <PromptLabLayout
                definition={definition}
                lessonTitle={lessonTitle}
                type={definition.type}
                grading={definition.grading}
                messages={messages}
                input={input}
                setInput={setInput}
                onSend={handleSend}
                isThinking={isThinking}
                editorCode={editorCode}
                setEditorCode={setEditorCode}
                language={definition.language}
                consoleOutput={consoleOutput}
                onRun={handleRun}
                isRunning={isRunning}
                onSubmit={handleSubmit}
                testResults={testResults}
                judgeResult={judgeResult}
                isJudging={isJudging}
                allPassed={allPassed}
                captureProject={!!lessonMeta?.captureProject}
                rubricSlots={specContext?.rubric}
                downloadReady={downloadReady}
                chatStaysOpen={chatStaysOpen}
                isPreparingDownload={isPreparingDownload}
                onDownloadApp={handleDownloadApp}
            />
        );
    }

    // Tester/admin shortcut: instantly satisfy this lesson's single completion
    // condition. markComplete() persists, reveals Next, and turns the lesson
    // green in the table. Hidden for normal users and once the lesson is passed.
    const showTesterSkip = isPrivilegedRole(user?.role) && !allPassed;

    // Built once, then either portaled into the footer between Back and Next
    // (the CD course renders LessonFooterNav, which exposes the slot) or shown
    // inline as a fallback on legacy pages that have no footer slot.
    const testerButton = showTesterSkip ? (
        <button
            type="button"
            onClick={markComplete}
            title="Visible to admin/tester accounts only"
            style={{
                border: "1px dashed #7C3AED",
                background: "#EDE9FE",
                color: "#7C3AED",
                borderRadius: 6,
                padding: "4px 10px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
            }}
        >
            {t("promptLab.testerMarkComplete")}
        </button>
    ) : null;

    // Replay pill for the HUD walkthrough. The tour auto-runs once per HUD type
    // and is then marked seen, so this is the only way back into it — it has to
    // stay visible. It is portaled into the navbar's dock rather than rendered
    // here, because floating it over the layout put it on top of the panel
    // headers. Layouts with no steps get no button (it would open an empty tour).
    const walkthroughButton = hudSteps[hud]?.length ? (
        <button
            type="button"
            className={`hud-wt-replay-btn${walkthroughSlot ? "" : " hud-wt-replay-btn--floating"}`}
            onClick={() => setReplaySignal((n) => n + 1)}
            title={t("promptLab.walkthroughTitle")}
        >
            <FontAwesomeIcon icon={faCircleQuestion} />
            <span className="hud-wt-replay-btn__label">{t("promptLab.walkthrough")}</span>
        </button>
    ) : null;

    // "Stuck?" pill — same ghost styling as the walkthrough pill, sitting just to
    // its left. It offers the three ways out of being stuck: have Claude explain
    // the exercise, have it done for you, or read the lesson's own hint.
    //
    // Where the help lands depends on the layout. Canvas, checklist and the default
    // lab talk to Claude through the hub's own chat; webLab, hotspot and editor each
    // own a private "Ask Claude" panel instead, so those are driven by a signal they
    // pick up (see askHelpSignal / solveSignal). TemplateStudio has no Claude at all,
    // which is why it gets no ask button — only solve and the hint.
    const layoutOwnsClaude = layout === "webLab" || layout === "hotspot" || layout === "editor";
    const canAskClaude = layoutOwnsClaude || layout !== "template";

    // "Do it for me" — fill the exercise in and pass the lesson, so a stuck student
    // can move on instead of stopping the course here. Layouts that own the work
    // (an editor, a canvas, a checklist, a screen to click) do the filling in
    // themselves off `solveSignal`; the default lab has Claude write the answer into
    // the chat, where a code lesson also lifts it into the editor. Either way the
    // lesson is marked complete afterwards, which is what unlocks Next.
    const layoutSolvesItself =
        layout === "webLab" ||
        layout === "editor" ||
        layout === "hotspot" ||
        layout === "canvas" ||
        layout === "checklist";
    const canSolve = !isPersonalLesson && !allPassed;

    const handleSolve = async () => {
        if (solving || isPersonalLesson) return;
        setSolving(true);
        try {
            if (layoutSolvesItself) setSolveSignal((n) => n + 1);
            else if (canAskClaude) await handleSend(t("promptLab.hintSolveMessage"));
            await markComplete();
        } finally {
            setSolving(false);
            closeHint();
        }
    };
    const hintButton = definition?.hint || canAskClaude ? (
        <span className="hud-hint" ref={hintRef}>
            <button
                type="button"
                ref={hintToggleRef}
                className="hud-wt-replay-btn hud-hint-btn"
                onClick={() => setHintOpen((open) => !open)}
                aria-expanded={hintOpen}
                aria-haspopup="dialog"
                title={t("promptLab.needHint")}
            >
                <FontAwesomeIcon icon={faLightbulb} />
                <span className="hud-wt-replay-btn__label">{t("promptLab.needHint")}</span>
            </button>
            {hintOpen && (
                <div className="hud-hint-pop" role="dialog" aria-label={t("promptLab.needHint")} ref={hintPopRef}>
                    <div className="hud-hint-actions">
                        {canAskClaude && (
                            <button
                                type="button"
                                className="hud-hint-action hud-hint-action--primary"
                                onClick={() => {
                                    closeHint();
                                    if (layoutOwnsClaude) setAskHelpSignal((n) => n + 1);
                                    else handleSend(t("promptLab.hintAskMessage"));
                                }}
                                disabled={isThinking || solving}
                            >
                                <FontAwesomeIcon icon={faComments} />
                                {t("promptLab.hintAsk")}
                            </button>
                        )}
                        {definition?.hint && !hintRevealed && (
                            <button
                                type="button"
                                className="hud-hint-action"
                                onClick={() => setHintRevealed(true)}
                            >
                                <FontAwesomeIcon icon={faEye} />
                                {t("promptLab.hintReveal")}
                            </button>
                        )}
                        {/* Last, and quietest: it is the way out of the lesson, not the
                            way through it. Ordered by what we want tried first, not
                            alphabetically — the escape hatch belongs at the bottom. */}
                        {canSolve && (
                            <button
                                type="button"
                                className="hud-hint-action hud-hint-action--escape"
                                onClick={handleSolve}
                                disabled={solving || isThinking}
                            >
                                <FontAwesomeIcon icon={faWandMagicSparkles} />
                                {solving ? t("promptLab.hintSolving") : t("promptLab.hintSolve")}
                            </button>
                        )}
                    </div>
                    {/* The two lessons whose answer IS the student's own idea: the app
                        idea (captureProject) and the build spec (downloadOnPerfect).
                        Solving those for them would hand back someone else's app. */}
                    {isPersonalLesson && <p className="hud-hint-note">{t("promptLab.hintSolveNotYours")}</p>}
                    {definition?.hint && hintRevealed && (
                        <div className="hud-hint-answer" ref={hintAnswerRef} tabIndex={-1}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{definition.hint}</ReactMarkdown>
                        </div>
                    )}
                </div>
            )}
        </span>
    ) : null;

    // Both pills share the dock. Without one (a hub mounted with no LessonNavBar)
    // they fall back to a floating cluster in the same corner.
    const dockButtons =
        hintButton || walkthroughButton ? (
            <>
                {hintButton}
                {walkthroughButton}
            </>
        ) : null;

    return (
        <>
            {/* The page's one h1, so the lesson can be found by its name. The
                panels below carry the visible titles. */}
            <h1 className="sr-only">{lessonTitle}</h1>
            {dockButtons &&
                (walkthroughSlot ? (
                    createPortal(dockButtons, walkthroughSlot)
                ) : (
                    <div className="hud-dock-floating">{dockButtons}</div>
                ))}
            {testerButton &&
                (testerSlot ? (
                    createPortal(testerButton, testerSlot)
                ) : (
                    <div style={{ display: "flex", justifyContent: "flex-end", padding: "8px 16px" }}>
                        {testerButton}
                    </div>
                ))}
            {layoutEl}
            <HudWalkthrough
                hud={hud}
                steps={hudSteps[hud]}
                forceOpenSignal={replaySignal}
                user={user}
            />
        </>
    );
};

export default PromptLabHubComponent;
